import React, { useEffect, useMemo, useState } from 'react'
import {SalesActivationPanel} from './platform/SalesActivationPanel'
import { adminService } from '../../services/admin'
import { useAccessContext } from '../../context/AccessContext'

const money = (cents?: number | null) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const label = (value?: string | null) => String(value || 'unassigned').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
const dateLabel = (value?: string | null) => value ? new Date(value).toLocaleDateString() : '-'

type SalesTab = 'overview' | 'lead-pipeline' | 'clients' | 'commissions' | 'payouts' | 'reps' | 'referral-links' | 'partners' | 'invites' | 'funnel' | 'attribution'

type Pagination = {
  page: number
  pageSize: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

type ListEnvelope<T = any> = {
  items: T[]
  pagination: Pagination
  sort?: { sortBy: string; sortDir: string }
  filters?: Record<string, any>
}

const emptyEnvelope = <T,>(pageSize = 10): ListEnvelope<T> => ({
  items: [],
  pagination: { page: 1, pageSize, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
})

const leadSources = ['website', 'linkedin', 'referral', 'partner', 'cold_outreach', 'conference', 'other']
const clientStatuses = ['trial', 'trialing', 'active', 'manual', 'past_due', 'paused', 'suspended', 'cancelled', 'expired']
const commissionStatuses = ['pending', 'approved', 'paid', 'void']
const payoutStatuses = ['draft', 'approved', 'paid', 'void']
const repStatuses = ['active', 'inactive']
const leadStatuses = ['new', 'contacted', 'qualified', 'demo_scheduled', 'proposal_sent', 'negotiating', 'won', 'lost']
const pageSizes = [10, 25, 50, 100]
const salesTabLabels: Record<SalesTab, string> = {
  overview: 'Sales Overview',
  'lead-pipeline': 'Lead Pipeline',
  clients: 'Clients',
  commissions: 'Commissions',
  payouts: 'Payouts',
  reps: 'Sales Reps',
  'referral-links': 'Referrals',
  partners: 'Partners',
  invites: 'Invites',
  funnel: 'Funnel',
  attribution: 'Client Attribution',
}

const nextCommissionStatuses = (status: string) => {
  if (status === 'pending') return ['pending', 'approved', 'void']
  if (status === 'approved') return ['approved', 'paid', 'void']
  if (status === 'paid') return ['paid']
  if (status === 'void') return ['void']
  return commissionStatuses
}

export const SalesWorkspace: React.FC = () => {
  const { accessContext, hasAnyPermission } = useAccessContext()
  const memberships = accessContext?.memberships || accessContext?.tenantMemberships || []
  const isTenantAdmin = memberships.some((membership) => membership.membership_type === 'tenant_admin')
  const isSystem = Boolean(accessContext?.isSystemAdmin || accessContext?.isSystemSuperAdmin)
  const canViewSales = Boolean(
    !isTenantAdmin && (
      accessContext?.uiCapabilities?.modules?.platformSales
      || accessContext?.canViewPlatformSales
      || accessContext?.canManagePlatformSales
      || accessContext?.canViewPlatformCommissions
      || accessContext?.canManagePlatformCommissions
      || accessContext?.isPlatformAdmin
      || isSystem
      || hasAnyPermission(['platform.sales.view', 'platform.sales.manage', 'platform.commissions.view', 'platform.commissions.manage', 'platform.billing.view'])
    )
  )
  const canManageAttribution = Boolean(
    !isTenantAdmin && (
      isSystem
      || accessContext?.canManagePlatformSales
      || hasAnyPermission(['tenant.platform.manage', 'platform.sales.manage'])
    )
  )
  const canViewCommissions = Boolean(
    !isTenantAdmin && (
      isSystem
      || accessContext?.canViewPlatformCommissions
      || accessContext?.canManagePlatformCommissions
      || hasAnyPermission(['tenant.platform.manage', 'platform.commissions.view', 'platform.commissions.manage'])
    )
  )
  const canManageCommissions = Boolean(
    !isTenantAdmin && (
      isSystem
      || accessContext?.canManagePlatformCommissions
      || hasAnyPermission(['tenant.platform.manage', 'platform.commissions.manage'])
    )
  )

  const [tab, setTab] = useState<SalesTab>('overview')
  const [dashboard, setDashboard] = useState<any>(null)
  const [clients, setClients] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [leads, setLeads] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [leadPipeline, setLeadPipeline] = useState<any>(null)
  const [commissions, setCommissions] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [payoutStatements, setPayoutStatements] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [payoutOverview, setPayoutOverview] = useState<any>(null)
  const [selectedPayoutStatement, setSelectedPayoutStatement] = useState<any>(null)
  const [payoutPreview, setPayoutPreview] = useState<any>(null)
  const [reps, setReps] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [referralLinks, setReferralLinks] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [partners, setPartners] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [linkClicks, setLinkClicks] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [linkConversions, setLinkConversions] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [repInvites, setRepInvites] = useState<ListEnvelope<any>>(emptyEnvelope())
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [selectedReferralLinkId, setSelectedReferralLinkId] = useState('')
  const [selectedInviteRepId, setSelectedInviteRepId] = useState('')
  const [repDraft, setRepDraft] = useState({ name: '', email: '', phone: '', status: 'active', commissionNotes: '' })
  const [partnerDraft, setPartnerDraft] = useState({ name: '', email: '', phone: '', partnerType: 'referral_partner', salesRepId: '' })
  const [linkDraft, setLinkDraft] = useState({ code: '', campaign: '', source: 'referral', medium: '', landingPath: '/signup', defaultPlanKey: '', partnerId: '', salesRepId: '' })
  const [inviteDraft, setInviteDraft] = useState({ salesRepId: '', email: '' })
  const [attributionDraft, setAttributionDraft] = useState({ salesRepId: '', leadSource: '', closeDate: '', estimatedArr: '', actualArr: '', notes: '' })
  const [payoutDraft, setPayoutDraft] = useState({ targetType: 'salesRep', salesRepId: '', partnerId: '', periodStart: '', periodEnd: '', notes: '' })
  const [leadView, setLeadView] = useState<'kanban' | 'table'>('kanban')
  const [leadFilters, setLeadFilters] = useState({ search: '', status: '', salesRepId: '', source: '', campaign: '', page: 1, pageSize: 25 })
  const [clientFilters, setClientFilters] = useState({ search: '', status: '', plan: '', salesRepId: '', leadSource: '', page: 1, pageSize: 10, sortBy: 'tenantName', sortDir: 'asc' })
  const [commissionFilters, setCommissionFilters] = useState({ search: '', status: '', salesRepId: '', tenantId: '', periodStart: '', periodEnd: '', page: 1, pageSize: 10, sortBy: 'period', sortDir: 'desc' })
  const [payoutFilters, setPayoutFilters] = useState({ status: '', salesRepId: '', partnerId: '', periodStart: '', periodEnd: '', page: 1, pageSize: 10 })
  const [repFilters, setRepFilters] = useState({ search: '', status: '', page: 1, pageSize: 10, sortBy: 'name', sortDir: 'asc' })
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const selectedClient = useMemo(() => clients.items.find((client) => client.tenantId === selectedTenantId) || clients.items[0] || null, [clients.items, selectedTenantId])
  const visiblePlans = useMemo(() => Array.from(new Set(clients.items.map((client) => client.planKey).filter(Boolean))), [clients.items])

  const load = async () => {
    if (!canViewSales) return
    setLoading(true)
    setError('')
    try {
      const [dashboardResponse, leadsResponse, clientsResponse, repsResponse, commissionsResponse] = await Promise.all([
        adminService.getPlatformSalesDashboard(),
        adminService.listPlatformSalesLeads(leadFilters),
        adminService.listPlatformSalesClients(clientFilters),
        adminService.listPlatformSalesReps(repFilters),
        canViewCommissions ? adminService.listPlatformSalesCommissions(commissionFilters) : Promise.resolve({ data: { commissions: emptyEnvelope() } }),
      ])
      const loadedClients = clientsResponse.data?.clients || emptyEnvelope()
      setDashboard(dashboardResponse.data?.dashboard || null)
      setLeadPipeline(leadsResponse.data?.pipeline || null)
      setLeads(leadsResponse.data?.leads || emptyEnvelope())
      setClients(loadedClients)
      setReps(repsResponse.data?.reps || emptyEnvelope())
      setCommissions(commissionsResponse.data?.commissions || emptyEnvelope())
      setSelectedTenantId((current) => (
        current && loadedClients.items.some((client: any) => client.tenantId === current)
          ? current
          : loadedClients.items[0]?.tenantId || ''
      ))
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error?.message || 'Unable to load sales workspace.')
    } finally {
      setLoading(false)
    }
  }

  const loadReferralData = async () => {
    if (!canViewSales) return
    try {
      const [linksResponse, partnersResponse] = await Promise.all([
        adminService.listPlatformReferralLinks({ pageSize: 50 }),
        adminService.listPlatformReferralPartners({ pageSize: 50 }),
      ])
      const loadedLinks = linksResponse.data?.referralLinks || emptyEnvelope()
      setReferralLinks(loadedLinks)
      setPartners(partnersResponse.data?.partners || emptyEnvelope())
      const linkId = selectedReferralLinkId || loadedLinks.items[0]?.id || ''
      setSelectedReferralLinkId(linkId)
      if (linkId) {
        const [clicksResponse, conversionsResponse] = await Promise.all([
          adminService.listPlatformReferralLinkClicks(linkId, { pageSize: 25 }),
          adminService.listPlatformReferralLinkConversions(linkId, { pageSize: 25 }),
        ])
        setLinkClicks(clicksResponse.data?.clicks || emptyEnvelope())
        setLinkConversions(conversionsResponse.data?.conversions || emptyEnvelope())
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load referral data.')
    }
  }

  useEffect(() => {
    load()
  }, [canViewSales, canViewCommissions, JSON.stringify(leadFilters), JSON.stringify(clientFilters), JSON.stringify(commissionFilters), JSON.stringify(repFilters)])

  useEffect(() => {
    loadReferralData()
  }, [canViewSales, selectedReferralLinkId])

  const loadPayoutData = async () => {
    if (!canViewCommissions) return
    try {
      const response = await adminService.listPlatformSalesPayouts(payoutFilters)
      const loadedStatements = response.data?.statements || emptyEnvelope()
      setPayoutOverview(response.data?.payouts || null)
      setPayoutStatements(loadedStatements)
      const statementId = selectedPayoutStatement?.id || loadedStatements.items[0]?.id || ''
      if (statementId) {
        const detail = await adminService.getPlatformSalesPayoutStatement(statementId)
        setSelectedPayoutStatement(detail.data?.statement || null)
      } else {
        setSelectedPayoutStatement(null)
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load payout statements.')
    }
  }

  useEffect(() => {
    loadPayoutData()
  }, [canViewCommissions, JSON.stringify(payoutFilters), selectedPayoutStatement?.id])

  useEffect(() => {
    const repId = selectedInviteRepId || reps.items[0]?.id || ''
    setSelectedInviteRepId(repId)
    setInviteDraft((current) => ({ ...current, salesRepId: current.salesRepId || repId }))
    if (!repId || !canViewSales) return
    adminService.listPlatformSalesRepInvites(repId, { pageSize: 50 })
      .then((response) => setRepInvites(response.data?.invites || emptyEnvelope()))
      .catch((err: any) => setError(err?.response?.data?.message || 'Unable to load sales rep invites.'))
  }, [canViewSales, selectedInviteRepId, reps.items.length])

  useEffect(() => {
    if (!selectedClient) return
    setAttributionDraft({
      salesRepId: selectedClient.salesRep?.id || '',
      leadSource: selectedClient.leadSource || '',
      closeDate: selectedClient.closeDate ? String(selectedClient.closeDate).slice(0, 10) : '',
      estimatedArr: selectedClient.estimatedArrCents ? String(selectedClient.estimatedArrCents) : '',
      actualArr: selectedClient.actualArrCents ? String(selectedClient.actualArrCents) : '',
      notes: selectedClient.attributionNotes || '',
    })
  }, [selectedClient?.tenantId])

  const saveAttribution = async () => {
    if (!selectedClient || !canManageAttribution) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.updatePlatformSalesClientAttribution(selectedClient.tenantId, {
        salesRepId: attributionDraft.salesRepId || null,
        leadSource: attributionDraft.leadSource || null,
        closeDate: attributionDraft.closeDate || null,
        estimatedArr: attributionDraft.estimatedArr ? Number(attributionDraft.estimatedArr) : null,
        actualArr: attributionDraft.actualArr ? Number(attributionDraft.actualArr) : null,
        notes: attributionDraft.notes || null,
      })
      setMessage('Client attribution saved.')
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save client attribution.')
    } finally {
      setLoading(false)
    }
  }

  const saveRep = async () => {
    if (!canManageAttribution || !repDraft.name.trim()) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.createPlatformSalesRep({
        name: repDraft.name.trim(),
        email: repDraft.email || null,
        phone: repDraft.phone || null,
        status: repDraft.status,
        commissionNotes: repDraft.commissionNotes || null,
      })
      setRepDraft({ name: '', email: '', phone: '', status: 'active', commissionNotes: '' })
      setMessage('Sales rep created.')
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to create sales rep.')
    } finally {
      setLoading(false)
    }
  }

  const savePartner = async () => {
    if (!canManageAttribution || !partnerDraft.name.trim()) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.createPlatformReferralPartner({
        name: partnerDraft.name.trim(),
        email: partnerDraft.email || null,
        phone: partnerDraft.phone || null,
        partnerType: partnerDraft.partnerType,
        salesRepId: partnerDraft.salesRepId || null,
      })
      setPartnerDraft({ name: '', email: '', phone: '', partnerType: 'referral_partner', salesRepId: '' })
      setMessage('Partner created.')
      await loadReferralData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to create partner.')
    } finally {
      setLoading(false)
    }
  }

  const saveReferralLink = async () => {
    if (!canManageAttribution) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.createPlatformReferralLink({
        ...linkDraft,
        partnerId: linkDraft.partnerId || null,
        salesRepId: linkDraft.salesRepId || null,
        defaultPlanKey: linkDraft.defaultPlanKey || null,
      })
      setLinkDraft({ code: '', campaign: '', source: 'referral', medium: '', landingPath: '/signup', defaultPlanKey: '', partnerId: '', salesRepId: '' })
      setMessage('Referral link created.')
      await loadReferralData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to create referral link.')
    } finally {
      setLoading(false)
    }
  }

  const deactivateReferralLink = async (linkId: string) => {
    if (!canManageAttribution) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.deactivatePlatformReferralLink(linkId)
      setMessage('Referral link deactivated.')
      await loadReferralData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to deactivate referral link.')
    } finally {
      setLoading(false)
    }
  }

  const createInvite = async () => {
    const repId = inviteDraft.salesRepId || selectedInviteRepId
    if (!canManageAttribution || !repId) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      const response = await adminService.createPlatformSalesRepInvite(repId, { email: inviteDraft.email || undefined })
      setMessage(response.data?.invite?.inviteCode ? `Invite created: ${response.data.invite.inviteCode}` : 'Invite created.')
      setInviteDraft({ salesRepId: repId, email: '' })
      const invites = await adminService.listPlatformSalesRepInvites(repId, { pageSize: 50 })
      setRepInvites(invites.data?.invites || emptyEnvelope())
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to create invite.')
    } finally {
      setLoading(false)
    }
  }

  const revokeInvite = async (inviteId: string) => {
    if (!canManageAttribution) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.revokePlatformSalesRepInvite(inviteId)
      setMessage('Invite revoked.')
      if (selectedInviteRepId) {
        const invites = await adminService.listPlatformSalesRepInvites(selectedInviteRepId, { pageSize: 50 })
        setRepInvites(invites.data?.invites || emptyEnvelope())
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to revoke invite.')
    } finally {
      setLoading(false)
    }
  }

  const updateRepStatus = async (repId: string, status: 'active' | 'inactive') => {
    if (!canManageAttribution) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.updatePlatformSalesRep(repId, { status })
      setMessage('Sales rep status updated.')
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to update sales rep.')
    } finally {
      setLoading(false)
    }
  }

  const updateCommission = async (commissionId: string, status: string) => {
    if (!canManageCommissions) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.updatePlatformSalesCommissionStatus(commissionId, { status })
      setMessage('Commission status updated.')
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to update commission.')
    } finally {
      setLoading(false)
    }
  }

  const updateLead = async (leadId: string, payload: any) => {
    setLoading(true)
    setMessage('')
    setError('')
    try {
      await adminService.updatePlatformSalesLead(leadId, payload)
      setMessage('Lead updated.')
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to update lead.')
    } finally {
      setLoading(false)
    }
  }

  const payoutPayload = () => ({
    partnerId: payoutDraft.targetType === 'partner' ? payoutDraft.partnerId || null : null,
    salesRepId: payoutDraft.targetType === 'salesRep' ? payoutDraft.salesRepId || null : null,
    periodStart: payoutDraft.periodStart,
    periodEnd: payoutDraft.periodEnd,
    notes: payoutDraft.notes || null,
  })

  const previewPayout = async () => {
    if (!canViewCommissions) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      const response = await adminService.previewPlatformSalesPayoutStatement(payoutPayload())
      setPayoutPreview(response.data?.preview || null)
      setMessage('Payout preview refreshed.')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to preview payout statement.')
    } finally {
      setLoading(false)
    }
  }

  const createPayoutStatement = async () => {
    if (!canManageCommissions) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      const response = await adminService.createPlatformSalesPayoutStatement(payoutPayload())
      setSelectedPayoutStatement(response.data?.statement || null)
      setPayoutPreview(null)
      setMessage('Draft payout statement created.')
      await loadPayoutData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to create payout statement.')
    } finally {
      setLoading(false)
    }
  }

  const loadPayoutStatement = async (statementId: string) => {
    if (!statementId) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.getPlatformSalesPayoutStatement(statementId)
      setSelectedPayoutStatement(response.data?.statement || null)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load payout statement.')
    } finally {
      setLoading(false)
    }
  }

  const transitionPayoutStatement = async (action: 'approve' | 'paid' | 'void') => {
    if (!canManageCommissions || !selectedPayoutStatement?.id) return
    setLoading(true)
    setMessage('')
    setError('')
    try {
      const response = action === 'approve'
        ? await adminService.approvePlatformSalesPayoutStatement(selectedPayoutStatement.id)
        : action === 'paid'
          ? await adminService.markPlatformSalesPayoutStatementPaid(selectedPayoutStatement.id)
          : await adminService.voidPlatformSalesPayoutStatement(selectedPayoutStatement.id)
      setSelectedPayoutStatement(response.data?.statement || null)
      setMessage('Payout statement updated.')
      await loadPayoutData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to update payout statement.')
    } finally {
      setLoading(false)
    }
  }

  const pageControls = (pagination: Pagination, onPage: (page: number) => void, onPageSize: (pageSize: number) => void) => (
    <div className="admin-panel-toolbar" style={{ alignItems: 'center' }}>
      <span className="helper">Page {pagination.page} of {pagination.totalPages} - {pagination.total} total</span>
      <div className="inline-group">
        <button type="button" className="btn btn-secondary btn-sm" disabled={!pagination.hasPreviousPage || loading} onClick={() => onPage(pagination.page - 1)}>Previous</button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!pagination.hasNextPage || loading} onClick={() => onPage(pagination.page + 1)}>Next</button>
        <select className="input" value={pagination.pageSize} onChange={(event) => onPageSize(Number(event.target.value))} aria-label="Page size">
          {pageSizes.map((size) => <option key={size} value={size}>{size} / page</option>)}
        </select>
      </div>
    </div>
  )

  if (!canViewSales) {
    return (
      <section className="panel workspace-panel stack">
        <h2 className="heading-lg">Sales workspace</h2>
        <p className="text-muted">Platform sales access is required.</p>
      </section>
    )
  }

  return (
    <section className="panel workspace-panel stack" data-testid="sales-workspace">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.35rem' }}>
          <h2 className="heading-lg" style={{ margin: 0 }}>Sales Administration</h2>
          <p className="text-muted">Review platform revenue, client attribution, commissions, and sales representative portfolios.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>{loading ? 'Refreshing...' : 'Refresh'}</button>
      </div>

      <div className="tab-list" role="tablist" aria-label="Sales workspace sections">
        {(['overview', 'lead-pipeline', 'clients', 'commissions', 'payouts', 'reps', 'referral-links', 'partners', 'invites', 'funnel', 'attribution'] as SalesTab[]).map((item) => (
          <button key={item} type="button" className={`tab-button ${tab === item ? 'active' : ''}`} onClick={() => setTab(item)}>{salesTabLabels[item]}</button>
        ))}
      </div>
      {loading ? <p className="text-muted">Loading sales workspace...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}
      {message ? <p className="success-text">{message}</p> : null}

      {tab === 'overview' ? (
        <div className="stack">
          <SalesActivationPanel canManage={canManageAttribution} leads={leads.items} clients={clients.items}/>
          <p className="text-muted">Stripe checkout and portal links require Stripe environment variables; manual billing data still appears here when Stripe is not configured.</p>
          <div className="setup-summary-grid">
            <div><span className="helper">MRR</span><strong>{money(dashboard?.totalMrrCents)}</strong></div>
            <div><span className="helper">ARR</span><strong>{money(dashboard?.totalArrCents)}</strong></div>
            <div><span className="helper">Pipeline ARR</span><strong>{money(leadPipeline?.pipelineArrCents)}</strong></div>
            <div><span className="helper">Weighted ARR</span><strong>{money(leadPipeline?.weightedArrCents)}</strong></div>
            <div><span className="helper">Expected MRR</span><strong>{money(leadPipeline?.expectedMrrCents)}</strong></div>
            <div><span className="helper">Active clients</span><strong>{dashboard?.activeClients || 0}</strong></div>
            <div><span className="helper">Trials</span><strong>{dashboard?.trialClients || 0}</strong></div>
            <div><span className="helper">Past due</span><strong>{dashboard?.pastDueClients || 0}</strong></div>
            <div><span className="helper">Pending commissions</span><strong>{money(dashboard?.pendingCommissionsCents)}</strong></div>
            <div><span className="helper">Paid commissions</span><strong>{money(dashboard?.paidCommissionsCents)}</strong></div>
          </div>
          <div className="billing-grid">
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Revenue by plan</h3>
              {(dashboard?.revenueByPlan || []).length ? (dashboard?.revenueByPlan || []).map((row: any) => (
                <div key={row.plan_key || row.planName} className="inline-group" style={{ justifyContent: 'space-between' }}>
                  <span>{row.plan_name || row.planName || row.planKey}</span><strong>{money(row.mrr_cents)} MRR</strong>
                </div>
              )) : <p className="text-muted">No plan revenue yet.</p>}
            </article>
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Revenue by lead source</h3>
              {(dashboard?.revenueByLeadSource || []).length ? (dashboard?.revenueByLeadSource || []).map((row: any) => (
                <div key={row.lead_source} className="inline-group" style={{ justifyContent: 'space-between' }}>
                  <span>{label(row.lead_source)}</span><strong>{money(row.mrr_cents)} MRR</strong>
                </div>
              )) : <p className="text-muted">No lead source revenue yet.</p>}
            </article>
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Revenue by sales rep</h3>
              {(dashboard?.revenueBySalesRep || []).length ? (dashboard?.revenueBySalesRep || []).map((row: any) => (
                <div key={row.sales_rep_id || 'unassigned'} className="inline-group" style={{ justifyContent: 'space-between' }}>
                  <span>{row.sales_rep_name}</span><strong>{money(row.mrr_cents)} MRR</strong>
                </div>
              )) : <p className="text-muted">No attributed sales rep revenue yet.</p>}
            </article>
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Revenue by referral partner</h3>
              {(dashboard?.revenueByReferralPartner || []).length ? (dashboard?.revenueByReferralPartner || []).map((row: any) => (
                <div key={row.partner_id || row.partner_name} className="inline-group" style={{ justifyContent: 'space-between' }}>
                  <span>{row.partner_name}</span><strong>{money(row.mrr_cents)} MRR</strong>
                </div>
              )) : <p className="text-muted">No referral partner revenue yet.</p>}
            </article>
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Revenue by industry</h3>
              {(dashboard?.revenueByIndustry || []).length ? (dashboard?.revenueByIndustry || []).map((row: any) => (
                <div key={row.industry} className="inline-group" style={{ justifyContent: 'space-between' }}>
                  <span>{label(row.industry)}</span><strong>{money(row.mrr_cents)} MRR</strong>
                </div>
              )) : <p className="text-muted">No industry revenue yet.</p>}
            </article>
          </div>
        </div>
      ) : null}

      {tab === 'lead-pipeline' ? (
        <div className="stack">
          <div className="setup-summary-grid">
            <div><span className="helper">New Leads</span><strong>{leadPipeline?.newLeads || 0}</strong></div>
            <div><span className="helper">Qualified Leads</span><strong>{leadPipeline?.qualifiedLeads || 0}</strong></div>
            <div><span className="helper">Demos Scheduled</span><strong>{leadPipeline?.demosScheduled || 0}</strong></div>
            <div><span className="helper">Proposals</span><strong>{leadPipeline?.proposals || 0}</strong></div>
            <div><span className="helper">Closed Won</span><strong>{leadPipeline?.closedWon || 0}</strong></div>
            <div><span className="helper">Closed Lost</span><strong>{leadPipeline?.closedLost || 0}</strong></div>
            <div><span className="helper">Pipeline ARR</span><strong>{money(leadPipeline?.pipelineArrCents)}</strong></div>
            <div><span className="helper">Weighted ARR</span><strong>{money(leadPipeline?.weightedArrCents)}</strong><span className="helper">{money(leadPipeline?.expectedMrrCents)} expected MRR</span></div>
          </div>
          <div className="form-grid">
            <label>Search<input className="input" value={leadFilters.search} onChange={(event) => setLeadFilters({ ...leadFilters, search: event.target.value, page: 1 })} /></label>
            <label>Status<select className="input" value={leadFilters.status} onChange={(event) => setLeadFilters({ ...leadFilters, status: event.target.value, page: 1 })}><option value="">All statuses</option>{leadStatuses.map((status) => <option key={status} value={status}>{label(status)}</option>)}</select></label>
            <label>Sales rep<select className="input" value={leadFilters.salesRepId} onChange={(event) => setLeadFilters({ ...leadFilters, salesRepId: event.target.value, page: 1 })}><option value="">All reps</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
            <label>Source<input className="input" value={leadFilters.source} onChange={(event) => setLeadFilters({ ...leadFilters, source: event.target.value, page: 1 })} /></label>
            <label>Campaign<input className="input" value={leadFilters.campaign} onChange={(event) => setLeadFilters({ ...leadFilters, campaign: event.target.value, page: 1 })} /></label>
            <label>View<select className="input" value={leadView} onChange={(event) => setLeadView(event.target.value as 'kanban' | 'table')}><option value="kanban">Kanban</option><option value="table">Table</option></select></label>
          </div>
          {leadView === 'kanban' ? (
            <div className="billing-grid">
              {leadStatuses.map((status) => {
                const stageLeads = leads.items.filter((lead) => lead.status === status)
                const stage = leadPipeline?.byStage?.[status]
                return (
                  <article key={status} className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
                    <h3 style={{ margin: 0 }}>{label(status)}</h3>
                    <p className="helper" style={{ margin: 0 }}>{stage?.leadCount || 0} leads · {money(stage?.arrCents)} ARR · {leadPipeline?.probabilities?.[status] ?? 0}%</p>
                    {stageLeads.length ? stageLeads.map((lead) => (
                      <div key={lead.id} className="panel stack" style={{ background: 'var(--surface)', borderRadius: 8, padding: '.85rem' }}>
                        <strong>{lead.companyName}</strong>
                        <span className="helper">{lead.contactName} · {lead.email}</span>
                        <span>{money(lead.estimatedArrCents)} ARR</span>
                        <span className="helper">{label(lead.source)} {lead.campaign ? `· ${lead.campaign}` : ''}</span>
                        <div className="inline-group">
                          <select className="input" value={lead.status} disabled={!canManageAttribution || loading} onChange={(event) => updateLead(lead.id, { status: event.target.value })}>
                            {leadStatuses.map((next) => <option key={next} value={next}>{label(next)}</option>)}
                          </select>
                        </div>
                      </div>
                    )) : <p className="text-muted">No leads in this stage.</p>}
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Company</th><th>Contact</th><th>Fit</th><th>Source</th><th>Campaign</th><th>Sales rep</th><th>Status</th><th>Estimated ARR</th><th>Created</th></tr></thead>
                <tbody>
                  {leads.items.map((lead) => (
                    <tr key={lead.id}>
                      <td><strong>{lead.companyName}</strong><br /><span className="helper">{lead.industry || '-'}</span></td>
                      <td>{lead.contactName}<br /><span className="helper">{lead.email}</span></td>
                      <td>{lead.workerCount ?? '-'} workers<br /><span className="helper">{lead.locationCount ?? '-'} locations</span></td>
                      <td>{label(lead.source)}</td>
                      <td>{lead.campaign || '-'}</td>
                      <td>
                        {canManageAttribution ? (
                          <select className="input" value={lead.salesRepId || ''} onChange={(event) => updateLead(lead.id, { salesRepId: event.target.value || null })}>
                            <option value="">Unassigned</option>
                            {reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}
                          </select>
                        ) : lead.salesRep?.name || 'Unassigned'}
                      </td>
                      <td>
                        {canManageAttribution ? (
                          <select className="input" value={lead.status} onChange={(event) => updateLead(lead.id, { status: event.target.value })}>
                            {leadStatuses.map((status) => <option key={status} value={status}>{label(status)}</option>)}
                          </select>
                        ) : label(lead.status)}
                      </td>
                      <td>{money(lead.estimatedArrCents)}</td>
                      <td>{dateLabel(lead.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!leads.items.length ? <p className="text-muted">No CRM leads yet.</p> : null}
          {pageControls(leads.pagination, (page) => setLeadFilters({ ...leadFilters, page }), (pageSize) => setLeadFilters({ ...leadFilters, pageSize, page: 1 }))}
        </div>
      ) : null}

      {tab === 'clients' ? (
        <div className="stack">
          <div className="form-grid">
            <label>Search<input className="input" value={clientFilters.search} onChange={(event) => setClientFilters({ ...clientFilters, search: event.target.value, page: 1 })} /></label>
            <label>Status<select className="input" value={clientFilters.status} onChange={(event) => setClientFilters({ ...clientFilters, status: event.target.value, page: 1 })}><option value="">All statuses</option>{clientStatuses.map((status) => <option key={status} value={status}>{label(status)}</option>)}</select></label>
            <label>Plan<select className="input" value={clientFilters.plan} onChange={(event) => setClientFilters({ ...clientFilters, plan: event.target.value, page: 1 })}><option value="">All plans</option>{visiblePlans.map((plan) => <option key={plan} value={plan}>{label(plan)}</option>)}</select></label>
            <label>Sales rep<select className="input" value={clientFilters.salesRepId} onChange={(event) => setClientFilters({ ...clientFilters, salesRepId: event.target.value, page: 1 })}><option value="">All reps</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
            <label>Lead source<select className="input" value={clientFilters.leadSource} onChange={(event) => setClientFilters({ ...clientFilters, leadSource: event.target.value, page: 1 })}><option value="">All sources</option>{leadSources.map((source) => <option key={source} value={source}>{label(source)}</option>)}</select></label>
          </div>
          {clients.items.length ? (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Client</th><th>Plan</th><th>Status</th><th>Base / Acceleration</th><th>Workforce scale</th><th>Location scale</th><th>Paid extras</th><th>Total MRR</th><th>Total ARR</th><th>Sales rep</th><th>Actions</th></tr></thead>
                <tbody>
                  {clients.items.map((client) => (
                    <tr key={client.tenantId}>
                      <td><strong>{client.tenantName}</strong><br /><span className="helper">{client.tenantSlug}</span></td>
                      <td>{client.planName || '-'}</td>
                      <td>{label(client.subscriptionStatus)}</td>
                      <td>{money(client.baseSubscriptionCents)}</td>
                      <td>{client.activeWorkers || 0} workers<br /><span className="helper">No capacity charge</span></td>
                      <td>{client.currentLocations || 0} locations<br /><span className="helper">No capacity charge</span></td>
                      <td>{money(client.commercialStackRevenueCents)}<br /><span className="helper">{money(client.addonRevenueCents)} add-ons · {money(client.productRevenueCents)} products</span></td>
                      <td>{money(client.mrrCents)}</td>
                      <td>{money(client.arrCents)}</td>
                      <td>{client.salesRep?.name || 'Unassigned'}</td>
                      <td>{canManageAttribution ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setSelectedTenantId(client.tenantId); setTab('attribution') }}>Edit</button> : <span className="helper">Read only</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-muted">No clients yet.</p>}
          {pageControls(clients.pagination, (page) => setClientFilters({ ...clientFilters, page }), (pageSize) => setClientFilters({ ...clientFilters, pageSize, page: 1 }))}
        </div>
      ) : null}

      {tab === 'commissions' ? (
        <div className="stack">
          {!canViewCommissions ? <p className="text-muted">Commission view permission is required.</p> : null}
          {canViewCommissions ? (
            <>
              <div className="form-grid">
                <label>Search<input className="input" value={commissionFilters.search} onChange={(event) => setCommissionFilters({ ...commissionFilters, search: event.target.value, page: 1 })} /></label>
                <label>Status<select className="input" value={commissionFilters.status} onChange={(event) => setCommissionFilters({ ...commissionFilters, status: event.target.value, page: 1 })}><option value="">All statuses</option>{commissionStatuses.map((status) => <option key={status} value={status}>{label(status)}</option>)}</select></label>
                <label>Sales rep<select className="input" value={commissionFilters.salesRepId} onChange={(event) => setCommissionFilters({ ...commissionFilters, salesRepId: event.target.value, page: 1 })}><option value="">All reps</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
                <label>Client<select className="input" value={commissionFilters.tenantId} onChange={(event) => setCommissionFilters({ ...commissionFilters, tenantId: event.target.value, page: 1 })}><option value="">All clients</option>{clients.items.map((client) => <option key={client.tenantId} value={client.tenantId}>{client.tenantName}</option>)}</select></label>
                <label>Period start<input className="input" type="date" value={commissionFilters.periodStart} onChange={(event) => setCommissionFilters({ ...commissionFilters, periodStart: event.target.value, page: 1 })} /></label>
                <label>Period end<input className="input" type="date" value={commissionFilters.periodEnd} onChange={(event) => setCommissionFilters({ ...commissionFilters, periodEnd: event.target.value, page: 1 })} /></label>
              </div>
              {commissions.items.length ? (
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead><tr><th>Period</th><th>Client</th><th>Invoice</th><th>Sales rep</th><th>Amount</th><th>Rate</th><th>Status</th><th>Paid</th><th>Actions</th></tr></thead>
                    <tbody>
                      {commissions.items.map((commission) => (
                        <tr key={commission.id}>
                          <td>{commission.commissionPeriod}</td>
                          <td>{commission.tenantName}</td>
                          <td>{commission.invoiceNumber}</td>
                          <td>{commission.salesRep?.name}</td>
                          <td>{money(commission.commissionCents)}</td>
                          <td>{(commission.commissionRate * 100).toFixed(1)}%</td>
                          <td>{label(commission.status)}</td>
                          <td>{dateLabel(commission.paidAt)}</td>
                          <td>
                            {canManageCommissions ? (
                              <select className="input" value={commission.status} disabled={loading} onChange={(event) => updateCommission(commission.id, event.target.value)}>
                                {nextCommissionStatuses(commission.status).map((status) => <option key={status} value={status}>{label(status)}</option>)}
                              </select>
                            ) : <span className="helper">Read only</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-muted">No commissions yet.</p>}
              {pageControls(commissions.pagination, (page) => setCommissionFilters({ ...commissionFilters, page }), (pageSize) => setCommissionFilters({ ...commissionFilters, pageSize, page: 1 }))}
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'payouts' ? (
        <div className="stack">
          {!canViewCommissions ? <p className="text-muted">Commission view permission is required.</p> : null}
          {canViewCommissions ? (
            <>
              <div className="setup-summary-grid">
                <div><span className="helper">Draft statements</span><strong>{payoutOverview?.summary?.draftCount || 0}</strong><span className="helper">{money(payoutOverview?.summary?.draftCents)}</span></div>
                <div><span className="helper">Approved statements</span><strong>{payoutOverview?.summary?.approvedCount || 0}</strong><span className="helper">{money(payoutOverview?.summary?.approvedCents)}</span></div>
                <div><span className="helper">Paid statements</span><strong>{payoutOverview?.summary?.paidCount || 0}</strong><span className="helper">{money(payoutOverview?.summary?.paidCents)}</span></div>
                <div><span className="helper">Payout accounts</span><strong>{payoutOverview?.accounts?.length || 0}</strong><span className="helper">Manual accounting only</span></div>
              </div>

              <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
                <h3 style={{ margin: 0 }}>Preview payout statement</h3>
                <div className="form-grid">
                  <label>Target type
                    <select className="input" value={payoutDraft.targetType} onChange={(event) => setPayoutDraft({ ...payoutDraft, targetType: event.target.value, partnerId: '', salesRepId: '' })}>
                      <option value="salesRep">Sales rep</option>
                      <option value="partner">Partner</option>
                    </select>
                  </label>
                  {payoutDraft.targetType === 'salesRep' ? (
                    <label>Sales rep<select className="input" value={payoutDraft.salesRepId} onChange={(event) => setPayoutDraft({ ...payoutDraft, salesRepId: event.target.value })}><option value="">Choose rep</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
                  ) : (
                    <label>Partner<select className="input" value={payoutDraft.partnerId} onChange={(event) => setPayoutDraft({ ...payoutDraft, partnerId: event.target.value })}><option value="">Choose partner</option>{partners.items.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></label>
                  )}
                  <label>Period start<input className="input" type="date" value={payoutDraft.periodStart} onChange={(event) => setPayoutDraft({ ...payoutDraft, periodStart: event.target.value })} /></label>
                  <label>Period end<input className="input" type="date" value={payoutDraft.periodEnd} onChange={(event) => setPayoutDraft({ ...payoutDraft, periodEnd: event.target.value })} /></label>
                </div>
                <label>Notes<textarea className="input" value={payoutDraft.notes} onChange={(event) => setPayoutDraft({ ...payoutDraft, notes: event.target.value })} /></label>
                <div className="inline-group">
                  <button type="button" className="btn btn-secondary" onClick={previewPayout} disabled={loading || !payoutDraft.periodStart || !payoutDraft.periodEnd || !(payoutDraft.salesRepId || payoutDraft.partnerId)}>Preview</button>
                  {canManageCommissions ? <button type="button" className="btn btn-primary" onClick={createPayoutStatement} disabled={loading || !payoutDraft.periodStart || !payoutDraft.periodEnd || !(payoutDraft.salesRepId || payoutDraft.partnerId)}>Create draft</button> : null}
                </div>
                {payoutPreview ? (
                  <div className="stack">
                    <div className="setup-summary-grid">
                      <div><span className="helper">Eligible lines</span><strong>{payoutPreview.lineCount || 0}</strong></div>
                      <div><span className="helper">Gross attributed</span><strong>{money(payoutPreview.grossRevenueAttributedCents)}</strong></div>
                      <div><span className="helper">Commission</span><strong>{money(payoutPreview.commissionAmountCents)}</strong></div>
                      <div><span className="helper">Net payout</span><strong>{money(payoutPreview.netPayoutAmountCents)}</strong></div>
                    </div>
                    {payoutPreview.hasNoRateLines ? <p className="text-muted">One or more partner lines have no payout rate configured and preview as zero.</p> : null}
                    <div className="table-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Type</th><th>Client</th><th>Invoice</th><th>Gross</th><th>Rate</th><th>Amount</th><th>Description</th></tr></thead>
                        <tbody>{(payoutPreview.lines || []).map((line: any, index: number) => (
                          <tr key={`${line.invoiceId || line.commissionId || index}`}>
                            <td>{label(line.lineType)}</td><td>{line.tenantName || line.tenantId || '-'}</td><td>{line.invoiceNumber || line.invoiceId || '-'}</td><td>{money(line.grossAmountCents)}</td><td>{line.rateBps === null ? '-' : `${(Number(line.rateBps || 0) / 100).toFixed(2)}%`}</td><td>{money(line.amountCents)}</td><td>{line.description}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </article>

              <div className="form-grid">
                <label>Status<select className="input" value={payoutFilters.status} onChange={(event) => setPayoutFilters({ ...payoutFilters, status: event.target.value, page: 1 })}><option value="">All statuses</option>{payoutStatuses.map((status) => <option key={status} value={status}>{label(status)}</option>)}</select></label>
                <label>Sales rep<select className="input" value={payoutFilters.salesRepId} onChange={(event) => setPayoutFilters({ ...payoutFilters, salesRepId: event.target.value, partnerId: '', page: 1 })}><option value="">All reps</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
                <label>Partner<select className="input" value={payoutFilters.partnerId} onChange={(event) => setPayoutFilters({ ...payoutFilters, partnerId: event.target.value, salesRepId: '', page: 1 })}><option value="">All partners</option>{partners.items.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></label>
                <label>Period start<input className="input" type="date" value={payoutFilters.periodStart} onChange={(event) => setPayoutFilters({ ...payoutFilters, periodStart: event.target.value, page: 1 })} /></label>
                <label>Period end<input className="input" type="date" value={payoutFilters.periodEnd} onChange={(event) => setPayoutFilters({ ...payoutFilters, periodEnd: event.target.value, page: 1 })} /></label>
              </div>
              {payoutStatements.items.length ? (
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead><tr><th>Statement</th><th>Target</th><th>Period</th><th>Status</th><th>Lines</th><th>Gross</th><th>Net payout</th><th>Actions</th></tr></thead>
                    <tbody>{payoutStatements.items.map((statement) => (
                      <tr key={statement.id}>
                        <td><strong>{statement.statementNumber}</strong><br /><span className="helper">{dateLabel(statement.createdAt)}</span></td>
                        <td>{statement.partner?.name || statement.salesRep?.name || '-'}</td>
                        <td>{dateLabel(statement.periodStart)} - {dateLabel(statement.periodEnd)}</td>
                        <td>{label(statement.status)}</td>
                        <td>{statement.lineCount}</td>
                        <td>{money(statement.grossRevenueAttributedCents)}</td>
                        <td>{money(statement.netPayoutAmountCents)}</td>
                        <td><button type="button" className="btn btn-secondary btn-sm" onClick={() => loadPayoutStatement(statement.id)}>View</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <p className="text-muted">No payout statements yet.</p>}
              {pageControls(payoutStatements.pagination, (page) => setPayoutFilters({ ...payoutFilters, page }), (pageSize) => setPayoutFilters({ ...payoutFilters, pageSize, page: 1 }))}

              {selectedPayoutStatement ? (
                <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
                  <div className="admin-panel-toolbar">
                    <div>
                      <h3 style={{ margin: 0 }}>{selectedPayoutStatement.statementNumber}</h3>
                      <p className="text-muted" style={{ margin: 0 }}>{selectedPayoutStatement.partner?.name || selectedPayoutStatement.salesRep?.name || 'Unassigned'} - {label(selectedPayoutStatement.status)}</p>
                    </div>
                    <div className="inline-group">
                      <a className="btn btn-secondary btn-sm" href={adminService.getPlatformSalesPayoutExportUrl(selectedPayoutStatement.id, 'csv')}>CSV</a>
                      <a className="btn btn-secondary btn-sm" href={adminService.getPlatformSalesPayoutExportUrl(selectedPayoutStatement.id, 'json')}>JSON</a>
                      {canManageCommissions && selectedPayoutStatement.status === 'draft' ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => transitionPayoutStatement('approve')}>Approve</button> : null}
                      {canManageCommissions && selectedPayoutStatement.status === 'approved' ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => transitionPayoutStatement('paid')}>Mark paid</button> : null}
                      {canManageCommissions && ['draft', 'approved'].includes(selectedPayoutStatement.status) ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => transitionPayoutStatement('void')}>Void</button> : null}
                    </div>
                  </div>
                  <div className="setup-summary-grid">
                    <div><span className="helper">Gross attributed</span><strong>{money(selectedPayoutStatement.grossRevenueAttributedCents)}</strong></div>
                    <div><span className="helper">Commission</span><strong>{money(selectedPayoutStatement.commissionAmountCents)}</strong></div>
                    <div><span className="helper">Adjustments</span><strong>{money(selectedPayoutStatement.adjustmentsAmountCents)}</strong></div>
                    <div><span className="helper">Net payout</span><strong>{money(selectedPayoutStatement.netPayoutAmountCents)}</strong></div>
                  </div>
                  <div className="table-scroll">
                    <table className="admin-table">
                      <thead><tr><th>Type</th><th>Client</th><th>Invoice</th><th>Commission</th><th>Gross</th><th>Rate</th><th>Amount</th><th>Description</th></tr></thead>
                      <tbody>{(selectedPayoutStatement.lines || []).map((line: any) => (
                        <tr key={line.id}>
                          <td>{label(line.lineType)}</td><td>{line.tenantName || line.tenantId || '-'}</td><td>{line.invoiceNumber || line.invoiceId || '-'}</td><td>{line.commissionId || '-'}</td><td>{money(line.grossAmountCents)}</td><td>{line.rateBps === null ? '-' : `${(Number(line.rateBps || 0) / 100).toFixed(2)}%`}</td><td>{money(line.amountCents)}</td><td>{line.description}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <div className="table-scroll">
                    <table className="admin-table">
                      <thead><tr><th>Event</th><th>Actor</th><th>Created</th></tr></thead>
                      <tbody>{(selectedPayoutStatement.events || []).map((event: any) => (
                        <tr key={event.id}><td>{label(event.eventType)}</td><td>{event.actorUserId || '-'}</td><td>{dateLabel(event.createdAt)}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </article>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'reps' ? (
        <div className="stack">
          {canManageAttribution ? (
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Create sales rep</h3>
              <div className="form-grid">
                <label>Name<input className="input" value={repDraft.name} onChange={(event) => setRepDraft({ ...repDraft, name: event.target.value })} /></label>
                <label>Email<input className="input" value={repDraft.email} onChange={(event) => setRepDraft({ ...repDraft, email: event.target.value })} /></label>
                <label>Phone<input className="input" value={repDraft.phone} onChange={(event) => setRepDraft({ ...repDraft, phone: event.target.value })} /></label>
                <label>Status<select className="input" value={repDraft.status} onChange={(event) => setRepDraft({ ...repDraft, status: event.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
              </div>
              <label>Commission notes<textarea className="input" value={repDraft.commissionNotes} onChange={(event) => setRepDraft({ ...repDraft, commissionNotes: event.target.value })} /></label>
              <button type="button" className="btn btn-primary" onClick={saveRep} disabled={loading || !repDraft.name.trim()}>Create rep</button>
            </article>
          ) : null}
          <div className="form-grid">
            <label>Search<input className="input" value={repFilters.search} onChange={(event) => setRepFilters({ ...repFilters, search: event.target.value, page: 1 })} /></label>
            <label>Status<select className="input" value={repFilters.status} onChange={(event) => setRepFilters({ ...repFilters, status: event.target.value, page: 1 })}><option value="">All statuses</option>{repStatuses.map((status) => <option key={status} value={status}>{label(status)}</option>)}</select></label>
          </div>
          {reps.items.length ? (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Rep</th><th>Email</th><th>Active clients</th><th>MRR managed</th><th>Pending commission</th><th>Paid commission</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {reps.items.map((rep) => (
                    <tr key={rep.id}>
                      <td><strong>{rep.name}</strong><br /><span className="helper">{rep.phone || 'No phone'}</span></td>
                      <td>{rep.email || '-'}</td>
                      <td>{rep.activeClients}</td>
                      <td>{money(rep.mrrManagedCents)}</td>
                      <td>{money(rep.pendingCommissionCents)}</td>
                      <td>{money(rep.paidCommissionCents)}</td>
                      <td>{label(rep.status)}</td>
                      <td>
                        {canManageAttribution ? (
                          <button type="button" className="btn btn-secondary btn-sm" disabled={loading} onClick={() => updateRepStatus(rep.id, rep.status === 'active' ? 'inactive' : 'active')}>
                            {rep.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                        ) : <span className="helper">Read only</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-muted">No sales reps yet.</p>}
          {pageControls(reps.pagination, (page) => setRepFilters({ ...repFilters, page }), (pageSize) => setRepFilters({ ...repFilters, pageSize, page: 1 }))}
        </div>
      ) : null}

      {tab === 'referral-links' ? (
        <div className="stack">
          {canManageAttribution ? (
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Create referral link</h3>
              <div className="form-grid">
                <label>Code<input className="input" value={linkDraft.code} onChange={(event) => setLinkDraft({ ...linkDraft, code: event.target.value })} placeholder="summer-referral" /></label>
                <label>Campaign<input className="input" value={linkDraft.campaign} onChange={(event) => setLinkDraft({ ...linkDraft, campaign: event.target.value })} /></label>
                <label>Source<input className="input" value={linkDraft.source} onChange={(event) => setLinkDraft({ ...linkDraft, source: event.target.value })} /></label>
                <label>Medium<input className="input" value={linkDraft.medium} onChange={(event) => setLinkDraft({ ...linkDraft, medium: event.target.value })} /></label>
                <label>Landing path<input className="input" value={linkDraft.landingPath} onChange={(event) => setLinkDraft({ ...linkDraft, landingPath: event.target.value })} /></label>
                <label>Default plan<input className="input" value={linkDraft.defaultPlanKey} onChange={(event) => setLinkDraft({ ...linkDraft, defaultPlanKey: event.target.value })} /></label>
                <label>Partner<select className="input" value={linkDraft.partnerId} onChange={(event) => setLinkDraft({ ...linkDraft, partnerId: event.target.value })}><option value="">None</option>{partners.items.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></label>
                <label>Sales rep<select className="input" value={linkDraft.salesRepId} onChange={(event) => setLinkDraft({ ...linkDraft, salesRepId: event.target.value })}><option value="">None</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
              </div>
              <button type="button" className="btn btn-primary" onClick={saveReferralLink} disabled={loading}>Create link</button>
            </article>
          ) : null}
          {referralLinks.items.length ? (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Code</th><th>Campaign</th><th>Source</th><th>Partner</th><th>Sales rep</th><th>Status</th><th>Uses</th><th>Conversions</th><th>Created</th><th>Actions</th></tr></thead>
                <tbody>
                  {referralLinks.items.map((link) => (
                    <tr key={link.id}>
                      <td><strong>{link.code}</strong><br /><span className="helper">{link.landingPath || '/signup'}</span></td>
                      <td>{link.campaign || '-'}</td>
                      <td>{link.source || '-'}</td>
                      <td>{link.partner?.name || '-'}</td>
                      <td>{link.salesRep?.name || '-'}</td>
                      <td>{label(link.status)}</td>
                      <td>{link.useCount}</td>
                      <td>{link.conversions}</td>
                      <td>{dateLabel(link.createdAt)}</td>
                      <td className="inline-group">
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard?.writeText(`${window.location.origin}${link.landingPath || '/signup'}?ref=${encodeURIComponent(link.code)}`)}>Copy</button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setSelectedReferralLinkId(link.id); setTab('funnel') }}>View</button>
                        {canManageAttribution && link.status === 'active' ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => deactivateReferralLink(link.id)}>Deactivate</button> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-muted">No referral links yet.</p>}
        </div>
      ) : null}

      {tab === 'partners' ? (
        <div className="stack">
          {canManageAttribution ? (
            <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h3 style={{ margin: 0 }}>Create partner</h3>
              <div className="form-grid">
                <label>Name<input className="input" value={partnerDraft.name} onChange={(event) => setPartnerDraft({ ...partnerDraft, name: event.target.value })} /></label>
                <label>Email<input className="input" value={partnerDraft.email} onChange={(event) => setPartnerDraft({ ...partnerDraft, email: event.target.value })} /></label>
                <label>Phone<input className="input" value={partnerDraft.phone} onChange={(event) => setPartnerDraft({ ...partnerDraft, phone: event.target.value })} /></label>
                <label>Type<select className="input" value={partnerDraft.partnerType} onChange={(event) => setPartnerDraft({ ...partnerDraft, partnerType: event.target.value })}>{['sales_rep','referral_partner','agency_partner','consultant','affiliate','other'].map((type) => <option key={type} value={type}>{label(type)}</option>)}</select></label>
                <label>Assigned sales rep<select className="input" value={partnerDraft.salesRepId} onChange={(event) => setPartnerDraft({ ...partnerDraft, salesRepId: event.target.value })}><option value="">None</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
              </div>
              <button type="button" className="btn btn-primary" onClick={savePartner} disabled={loading || !partnerDraft.name.trim()}>Create partner</button>
            </article>
          ) : null}
          {partners.items.length ? (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Type</th><th>Email</th><th>Status</th><th>Assigned sales rep</th><th>Created</th></tr></thead>
                <tbody>{partners.items.map((partner) => (
                  <tr key={partner.id}><td><strong>{partner.name}</strong></td><td>{label(partner.partnerType)}</td><td>{partner.email || '-'}</td><td>{label(partner.status)}</td><td>{partner.salesRep?.name || '-'}</td><td>{dateLabel(partner.createdAt)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : <p className="text-muted">No partners yet.</p>}
        </div>
      ) : null}

      {tab === 'invites' ? (
        <div className="stack">
          <div className="form-grid">
            <label>Sales rep<select className="input" value={inviteDraft.salesRepId || selectedInviteRepId} onChange={(event) => { setSelectedInviteRepId(event.target.value); setInviteDraft({ ...inviteDraft, salesRepId: event.target.value }) }}><option value="">Choose rep</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
            <label>Email<input className="input" value={inviteDraft.email} onChange={(event) => setInviteDraft({ ...inviteDraft, email: event.target.value })} placeholder="rep@example.com" /></label>
          </div>
          {canManageAttribution ? <button type="button" className="btn btn-primary" onClick={createInvite} disabled={loading || !(inviteDraft.salesRepId || selectedInviteRepId)}>Create invite</button> : null}
          {repInvites.items.length ? (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Rep</th><th>Email</th><th>Status</th><th>Expires</th><th>Accepted</th><th>Actions</th></tr></thead>
                <tbody>{repInvites.items.map((invite) => (
                  <tr key={invite.id}><td>{invite.salesRepName || '-'}</td><td>{invite.email}</td><td>{label(invite.status)}</td><td>{dateLabel(invite.expiresAt)}</td><td>{dateLabel(invite.acceptedAt)}</td><td>{canManageAttribution && invite.status === 'pending' ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => revokeInvite(invite.id)}>Revoke</button> : <span className="helper">-</span>}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : <p className="text-muted">No invites for this rep yet.</p>}
        </div>
      ) : null}

      {tab === 'funnel' ? (
        <div className="stack">
          <label>Referral link<select className="input" value={selectedReferralLinkId} onChange={(event) => setSelectedReferralLinkId(event.target.value)}>{referralLinks.items.map((link) => <option key={link.id} value={link.id}>{link.code}</option>)}</select></label>
          <div className="setup-summary-grid">
            <div><span className="helper">Clicks</span><strong>{linkClicks.pagination.total}</strong></div>
            <div><span className="helper">Clients</span><strong>{linkConversions.pagination.total}</strong></div>
            <div><span className="helper">MRR</span><strong>{money(linkConversions.items.reduce((sum, conversion) => sum + Number(conversion.mrrCents || 0), 0))}</strong></div>
            <div><span className="helper">ARR</span><strong>{money(linkConversions.items.reduce((sum, conversion) => sum + Number(conversion.arrCents || 0), 0))}</strong></div>
            <div><span className="helper">Revenue Attributed</span><strong>{money(linkConversions.items.reduce((sum, conversion) => sum + Number(conversion.revenueAttributedCents || 0), 0))}</strong></div>
            <div><span className="helper">Paid customers</span><strong>{commissions.items.filter((commission) => linkConversions.items.some((conversion) => conversion.tenantId === commission.tenantId) && commission.status === 'paid').length}</strong></div>
            <div><span className="helper">Commissions generated</span><strong>{commissions.items.filter((commission) => linkConversions.items.some((conversion) => conversion.tenantId === commission.tenantId)).length}</strong></div>
          </div>
          <div className="table-scroll">
            <table className="admin-table">
              <thead><tr><th>Tenant</th><th>Lead source</th><th>MRR</th><th>ARR</th><th>Revenue Attributed</th><th>Sales rep</th><th>Partner</th><th>Converted</th></tr></thead>
              <tbody>{linkConversions.items.map((conversion) => (
                <tr key={conversion.id}><td>{conversion.tenantName || conversion.tenantId}</td><td>{label(conversion.leadSource)}</td><td>{money(conversion.mrrCents)}</td><td>{money(conversion.arrCents)}</td><td>{money(conversion.revenueAttributedCents)}</td><td>{conversion.salesRep?.name || '-'}</td><td>{conversion.partner?.name || '-'}</td><td>{dateLabel(conversion.convertedAt)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'attribution' ? (
        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Revenue Attribution</h3>
          {!canManageAttribution ? <p className="text-muted">Attribution management permission is required to edit records.</p> : null}
          {selectedClient ? (
            <>
              <label>Client
                <select className="input" value={selectedClient?.tenantId || ''} onChange={(event) => setSelectedTenantId(event.target.value)}>
                  {clients.items.map((client) => <option key={client.tenantId} value={client.tenantId}>{client.tenantName}</option>)}
                </select>
              </label>
              <div className="form-grid">
                <label>Sales rep<select className="input" value={attributionDraft.salesRepId} disabled={!canManageAttribution} onChange={(event) => setAttributionDraft({ ...attributionDraft, salesRepId: event.target.value })}><option value="">Unassigned</option>{reps.items.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select></label>
                <label>Lead source<select className="input" value={attributionDraft.leadSource} disabled={!canManageAttribution} onChange={(event) => setAttributionDraft({ ...attributionDraft, leadSource: event.target.value })}><option value="">Unassigned</option>{leadSources.map((source) => <option key={source} value={source}>{label(source)}</option>)}</select></label>
                <label>Close date<input className="input" type="date" value={attributionDraft.closeDate} disabled={!canManageAttribution} onChange={(event) => setAttributionDraft({ ...attributionDraft, closeDate: event.target.value })} /></label>
                <label>Estimated ARR cents<input className="input" type="number" min="0" value={attributionDraft.estimatedArr} disabled={!canManageAttribution} onChange={(event) => setAttributionDraft({ ...attributionDraft, estimatedArr: event.target.value })} /></label>
                <label>Actual ARR cents<input className="input" type="number" min="0" value={attributionDraft.actualArr} disabled={!canManageAttribution} onChange={(event) => setAttributionDraft({ ...attributionDraft, actualArr: event.target.value })} /></label>
              </div>
              <label>Notes<textarea className="input" value={attributionDraft.notes} disabled={!canManageAttribution} onChange={(event) => setAttributionDraft({ ...attributionDraft, notes: event.target.value })} /></label>
              <button type="button" className="btn btn-primary" onClick={saveAttribution} disabled={!canManageAttribution || loading || !selectedClient}>Save attribution</button>
            </>
          ) : <p className="text-muted">No clients yet.</p>}
        </article>
      ) : null}
    </section>
  )
}
